import React, { useMemo } from "react";
import { fmtMoney } from "@/lib/format";

/**
 * Lightweight inline SVG sparkline chart of the user's balance over the last
 * N days (default 30). Computes a daily balance series by walking backwards
 * from the current balance through every transaction.
 *
 * Pure CSS/SVG — no charting library required.
 */
export default function BalanceSparkline({ transactions = [], currentBalance = 0, days = 30, height = 110, currency = "EUR" }) {
  const series = useMemo(() => buildDailySeries(transactions, currentBalance, days), [transactions, currentBalance, days]);

  if (!series || series.length === 0) return null;

  const min = Math.min(...series.map((p) => p.balance));
  const max = Math.max(...series.map((p) => p.balance));
  const range = Math.max(max - min, 1);

  const W = 600;
  const H = height;
  const padX = 4;
  const padY = 14;

  const points = series.map((p, i) => {
    const x = padX + (i / (series.length - 1 || 1)) * (W - padX * 2);
    const y = padY + (1 - (p.balance - min) / range) * (H - padY * 2);
    return { ...p, x, y };
  });

  const linePath = points.map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`)).join(" ");
  const areaPath = `${linePath} L${points[points.length - 1].x},${H} L${points[0].x},${H} Z`;

  const last = points[points.length - 1];
  const first = points[0];
  const delta = last.balance - first.balance;
  const deltaPct = first.balance !== 0 ? (delta / Math.abs(first.balance)) * 100 : 0;
  const trendColor = delta >= 0 ? "#16A34A" : "#DC2626";

  return (
    <div className="w-full">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10px] font-mono-fin uppercase tracking-widest text-slate-500">{days}d</div>
        <div className={`text-xs font-mono-fin ${delta >= 0 ? "text-green-600" : "text-red-600"}`}>
          {delta >= 0 ? "▲" : "▼"} {fmtMoney(Math.abs(delta), currency)}
          <span className="text-slate-400 ml-1">({deltaPct >= 0 ? "+" : ""}{deltaPct.toFixed(1)}%)</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" style={{ height }}>
        <defs>
          <linearGradient id="bal-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={trendColor} stopOpacity="0.25" />
            <stop offset="100%" stopColor={trendColor} stopOpacity="0" />
          </linearGradient>
          <linearGradient id="bal-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#7C3AED" />
            <stop offset="60%" stopColor="#4F46E5" />
            <stop offset="100%" stopColor={trendColor} />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#bal-area)" />
        <path d={linePath} fill="none" stroke="url(#bal-line)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={last.x} cy={last.y} r="4" fill={trendColor} stroke="#fff" strokeWidth="2" />
      </svg>
    </div>
  );
}

function buildDailySeries(transactions, currentBalance, days) {
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  // Group by yyyy-mm-dd. We walk backwards from "today's balance" subtracting
  // each transaction's net effect to compute the balance at the start of each day.
  const byDay = {};
  for (const tx of transactions || []) {
    const ts = tx.executed_at || tx.created_at;
    if (!ts) continue;
    const d = new Date(ts);
    if (isNaN(d.getTime())) continue;
    const key = d.toISOString().slice(0, 10);
    byDay[key] = (byDay[key] || 0) + (tx.amount || 0);
  }

  const series = [];
  let runningBalance = Number(currentBalance) || 0;
  for (let i = 0; i < days; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    series.push({ date: key, balance: runningBalance });
    runningBalance -= byDay[key] || 0;
  }
  return series.reverse();
}
